import React, { createContext, useContext, useEffect, useState } from 'react'
import { normalizeDomainForDatabase } from '@/utils/domainUtils'

interface AgencyPublic {
  id: string
  domain: string
  company_name: string
  logo_url: string | null
  logo_url_light: string | null
  is_active: boolean
}

interface AgencyContextType {
  agency: AgencyPublic | null
  loading: boolean
  error: string | null
  refetchAgency: () => Promise<void>
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined)

export const useAgency = () => {
  const context = useContext(AgencyContext)
  if (context === undefined) {
    throw new Error('useAgency must be used within an AgencyProvider')
  }
  return context
}

interface AgencyProviderProps {
  children: React.ReactNode
}

/** Edge Functions podem passar de 5s no cold start; timeouts curtos geram AbortError esperado. */
const AGENCY_FETCH_TIMEOUT_MS = 15_000

export const AgencyProvider: React.FC<AgencyProviderProps> = ({ children }) => {
  const [agency, setAgency] = useState<AgencyPublic | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getCurrentDomainVariations = (): string[] => {
    return normalizeDomainForDatabase(window.location.hostname)
  }

  const fetchAgencyByDomain = async (): Promise<void> => {
    const cacheKey = 'agency_cache'
    const cacheTimeKey = 'agency_cache_time'
    const cacheDuration = 5 * 60 * 1000

    try {
      setLoading(true)
      setError(null)

      const cachedData = sessionStorage.getItem(cacheKey)
      const cachedTime = sessionStorage.getItem(cacheTimeKey)

      if (cachedData && cachedTime) {
        const age = Date.now() - parseInt(cachedTime, 10)
        if (age < cacheDuration) {
          console.log('Using cached agency data')
          setAgency(JSON.parse(cachedData))
          setLoading(false)
          return
        }
      }

      const domainVariations = getCurrentDomainVariations()
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

      console.log('Tentando buscar agência para domínios:', domainVariations)
      console.log('Is localhost?', isLocalhost)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase não está configurado')
      }

      let agency: AgencyPublic | null = null
      let lastError: any = null

      const fetchWithRetry = async (domain: string, retries = 2): Promise<AgencyPublic | null> => {
        for (let i = 0; i <= retries; i++) {
          try {
            console.log(`Tentativa ${i + 1} - Buscando agência para domínio: ${domain}`)

            const controller = new AbortController()
            const timeoutId = setTimeout(() => {
              controller.abort(
                new DOMException(`Timeout após ${AGENCY_FETCH_TIMEOUT_MS}ms (get-agency-by-domain)`, 'AbortError')
              )
            }, AGENCY_FETCH_TIMEOUT_MS)

            let response: Response
            try {
              response = await fetch(
                `${supabaseUrl}/functions/v1/get-agency-by-domain?domain=${encodeURIComponent(domain)}`,
                {
                  method: 'GET',
                  headers: {
                    'apikey': supabaseAnonKey,
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'Content-Type': 'application/json',
                  },
                  signal: controller.signal,
                }
              )
            } finally {
              clearTimeout(timeoutId)
            }

            if (!response.ok) {
              if (response.status === 404) {
                console.warn(`Agência não encontrada para domínio: ${domain}`)
                return null
              }
              throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const result = await response.json()

            if (result.success && result.agency) {
              console.log(`Agência encontrada para domínio ${domain}:`, result.agency)
              return result.agency
            }

            return null
          } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
              console.warn(`Timeout ao buscar domínio ${domain} (tentativa ${i + 1})`)
            } else {
              console.warn(`Erro ao buscar domínio ${domain} (tentativa ${i + 1}):`, err)
            }

            if (i < retries) {
              await new Promise(resolve => setTimeout(resolve, 1000))
            } else {
              throw err
            }
          }
        }
        return null
      }

      for (const domain of domainVariations) {
        try {
          const result = await fetchWithRetry(domain)
          if (result) {
            agency = result
            break
          }
        } catch (err) {
          console.warn(`Erro inesperado ao buscar domínio ${domain}:`, err)
          lastError = err
          continue
        }
      }

      if (!agency && isLocalhost) {
        console.warn('Rodando em localhost - buscando primeira agencia ativa para desenvolvimento')
        try {
          const result = await fetchWithRetry('localhost')
          if (result) {
            console.log('✅ Usando agência default para desenvolvimento:', result)
            agency = result
          }
        } catch (err) {
          console.error('❌ Erro ao buscar agência default:', err)
          lastError = err
        }
      }

      if (!agency) {
        const currentDomain = window.location.hostname
        console.error('Nenhuma agência encontrada para os domínios:', domainVariations)

        setError('Agência Não Encontrada')
        setAgency(null)
        return
      }

      sessionStorage.setItem(cacheKey, JSON.stringify(agency))
      sessionStorage.setItem(cacheTimeKey, Date.now().toString())

      setAgency(agency)
      console.log('Agência carregada com sucesso:', agency)
    } catch (err) {
      console.error('Erro inesperado ao buscar agência:', err)
      setError(`Erro inesperado: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
      setAgency(null)
    } finally {
      setLoading(false)
    }
  }

  const refetchAgency = async (): Promise<void> => {
    await fetchAgencyByDomain()
  }

  useEffect(() => {
    fetchAgencyByDomain()
  }, [])

  const value: AgencyContextType = {
    agency,
    loading,
    error,
    refetchAgency,
  }

  return (
    <AgencyContext.Provider value={value}>
      {children}
    </AgencyContext.Provider>
  )
}